// src/pages/profile-settings/patient/ChangePasscode.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import Button from "@/components/ui/Button.jsx";
import InputOtp from "@/components/ui/InputOtp.jsx";
import supabase from "@/services/supabaseClient";
import { setMyPasscode } from "@/services/passcodeService.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  getLoginSecurityBasePath,
  getProfileTableForSecurity,
  hasPasscodeConfigured,
} from "@/features/security/accountSecurityState.js";

/**
 * ChangePasscode
 *
 * Final flow:
 * 1) Enter your AfriMed™ password (re-auth)
 * 2) Enter new 6-digit passcode + confirm
 *
 * On success: SnackBar "Passcode updated" then navigate back.
 * OTP-to-phone is handled separately under Two factor authentication.
 */
export default function ChangePasscode() {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const fromPasswordCreate = location.state?.fromPasswordCreate === true;
  const auth = useAuth() || {};
  const user = auth.user || null;
  const profile = auth.profile || null;
  const refreshProfile = auth.refreshProfile;
  const hasPasscode = hasPasscodeConfigured(profile);
  const mustCreatePasswordFirst = false;
  const requiresPasswordGate = false;
  const basePath = getLoginSecurityBasePath(profile, location.pathname);
  const profileTable = getProfileTableForSecurity(profile, location.pathname);

  const [step, setStep] = React.useState(requiresPasswordGate ? "password" : "new"); // "password" | "new"

  const [password, setPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");

  const [newCode, setNewCode] = React.useState("");
  const [confirmCode, setConfirmCode] = React.useState("");
  const [codeError, setCodeError] = React.useState("");

  const [showPassword, setShowPassword] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState("");

  function handleClose() {
    nav(basePath);
  }

  function goToPasswordSetup() {
    nav(`${basePath}/change-password`, {
      state: {
        fromPasscodeGate: true,
        returnTo: location.pathname,
        returnState: location.state,
      },
    });
  }

  function openSnack(message) {
    setSnackbar(message);
    window.clearTimeout(openSnack._t);
    // keep it on-screen long enough to read
    openSnack._t = window.setTimeout(() => setSnackbar(""), 5000);
  }

  // --- STEP 1: verify current password --------------------------------------

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    if (!password || submitting) return;

    setSubmitting(true);
    setPasswordError("");

    try {
      if (!user?.email) {
        throw new Error("We couldn't confirm your password for this account.");
      }
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });
      if (error) throw error;
      setStep("new");
    } catch {
      setPasswordError("Incorrect password. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // --- STEP 2: set new passcode ---------------------------------------------

  async function handleNewCodeSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    if (newCode.length !== 6 || confirmCode.length !== 6) {
      setCodeError(t("settings.passcode.errors.enterBoth", "Enter a 6-digit code in both fields."));
      return;
    }

    if (newCode !== confirmCode) {
      setCodeError(t("settings.passcode.errors.mismatch", "The codes don’t match. Check and try again."));
      return;
    }

    setSubmitting(true);
    setCodeError("");

    try {
      await setMyPasscode({
        profileTable,
        passcode: newCode,
      });

      if (typeof refreshProfile === "function") {
        try {
          await refreshProfile();
        } catch {
          // keep UI moving even if profile refresh lags
        }
      }

      openSnack(
        hasPasscode
          ? t("settings.passcode.updated", "Passcode updated")
          : t("settings.passcode.created", "Passcode created"),
      );
      // Let the SnackBar show briefly, then go back
      setTimeout(() => {
        nav(basePath);
      }, 600);
    } catch {
      setCodeError(
        t(
          "settings.passcode.errors.save",
          "We couldn’t update your passcode right now. Please try again.",
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  // --- RENDERERS ------------------------------------------------------------

  function renderPasswordStep() {
    return (
      <form className="mt-4 space-y-6" onSubmit={handlePasswordSubmit}>
        <div className="space-y-2">
          <h1 className="text-headline-small text-on-surface">
            Confirm password to change passcode
          </h1>
          <p className="text-body-medium text-on-surface-var">
            Enter your AfriMed™ password if you have already created one. If you
            do not have a password yet, create one first.
          </p>
        </div>

        <div>
          <div className="relative">
            <TextField56
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              tone="surface"
              className="pr-10"
            />

            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center"
            >
              <img
                src={
                  showPassword
                    ? "/icons/list-items/visibility.svg"
                    : "/icons/list-items/visibility_off.svg"
                }
                alt=""
                className="w-5 h-5"
              />
            </button>
          </div>
          {passwordError && (
            <p className="mt-2 text-body-small text-[rgb(var(--afrimed-error))]">
              {passwordError}
            </p>
          )}
        </div>

        <div className="space-y-4 pt-3">
          <Button
            type="submit"
            fullWidth
            loading={submitting}
            disabled={!password}
          >
            Confirm
          </Button>

          <Button
            type="button"
            variant="tonal"
            fullWidth
            onClick={goToPasswordSetup}
          >
            Create or manage password
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => nav(`${basePath}/change-password`, {
              state: {
                fromPasscodeGate: true,
                resetPassword: true,
                returnTo: location.pathname,
                returnState: location.state,
              },
            })}
          >
            Forgot password
          </Button>

          {/* AfriMed ghost button */}
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  function renderPasswordRequiredStep() {
    return (
      <div className="mt-4 space-y-6">
        <div className="space-y-2">
          <h1 className="text-headline-small text-on-surface">
            Create password first
          </h1>
          <p className="text-body-medium text-on-surface-var">
            You need an AfriMed™ password before you can create or change a
            passcode.
          </p>
        </div>

        <div className="space-y-4 pt-3">
          <Button
            type="button"
            fullWidth
            onClick={goToPasswordSetup}
          >
            Create password
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  function renderNewCodeStep() {
    return (
      <form className="mt-4 space-y-6" onSubmit={handleNewCodeSubmit}>
        <div className="space-y-2">
          <h1 className="text-headline-small text-on-surface">
            {hasPasscode
              ? t("settings.passcode.changeTitle", "Choose a new passcode")
              : t("settings.passcode.createTitle", "Create a passcode")}
          </h1>
          <p className="text-body-medium text-on-surface-var">
            {hasPasscode
              ? t(
                  "settings.passcode.changeCopy",
                  "Use a 6-digit code that's easy for you to remember but hard for others to guess.",
                )
              : t(
                  "settings.passcode.createCopy",
                  "Protect your AfriMed™ account with a 6-digit passcode.",
                )}
          </p>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-body-large text-on-surface">
              {t("settings.passcode.newLabel", "New passcode")}
            </label>
            <InputOtp length={6} value={newCode} onChange={setNewCode} />
          </div>

          <div className="space-y-2">
            <label className="text-body-large text-on-surface">
              {t("settings.passcode.confirmLabel", "Confirm passcode")}
            </label>
            <InputOtp
              length={6}
              value={confirmCode}
              onChange={setConfirmCode}
            />
          </div>

          {codeError && (
            <p className="text-body-small text-[rgb(var(--afrimed-error))]">
              {codeError}
            </p>
          )}
        </div>

        <div className="space-y-4 pt-3">
          <Button
            type="submit"
            fullWidth
            loading={submitting}
            disabled={
              newCode.length !== 6 || confirmCode.length !== 6 || submitting
            }
          >
            {hasPasscode
              ? t("settings.passcode.saveAction", "Save passcode")
              : t("settings.passcode.createAction", "Create passcode")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => (requiresPasswordGate ? setStep("password") : handleClose())}
          >
            {requiresPasswordGate ? t("common.back", "Back") : t("common.cancel", "Cancel")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter close onBack={handleClose} />

      {mustCreatePasswordFirst
        ? renderPasswordRequiredStep()
        : step === "password" && requiresPasswordGate
          ? renderPasswordStep()
          : renderNewCodeStep()}

      <Snackbar open={!!snackbar} onClose={() => setSnackbar("")}>
        {snackbar}
      </Snackbar>
    </SettingsScreenShell>
  );
}
